"use client";

import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import FormWrapperDefault from "src/lib/components/form-wrapper/default";
import { ValidationType } from "src/lib/utils";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type ContactFormContentPosition = "left" | "right";

type ContactFormViewportData = {
  backgroundImage?: string;
  backgroundColor?: string;
  title?: string;
  heading1?: string;
  description?: string;
  contentPosition?: ContactFormContentPosition;
  submitButtonText?: string;
  submitButtonColor?: string;
};

export interface VariantProps extends ContactFormViewportData {
  responsive?: ResponsivePropsConfig<ContactFormViewportData>;
}

const DEFAULT_BG = "#f3f3f3";
const DEFAULT_SUBMIT_COLOR = "#2e6b3e";

const CONTACT_FORM_FIELDS = [
  {
    name: "fullName",
    colSpan: { laptop: 6 },
    variant: "default",
    blockName: "text-field",
    validation: undefined,
    textFieldProps: {
      label: "Họ tên người liên hệ *",
      required: true,
      requiredMessage: "Vui lòng điền họ tên",
    },
  },
  {
    name: "phone",
    colSpan: { laptop: 6 },
    variant: "phone",
    blockName: "text-field",
    validation: ValidationType.phone,
    textFieldProps: {
      label: "Số điện thoại *",
      required: true,
      requiredMessage: "Vui lòng điền số điện thoại",
    },
  },
  {
    name: "email",
    colSpan: { laptop: 12 },
    variant: "default",
    blockName: "text-field",
    validation: ValidationType.email,
    textFieldProps: {
      label: "Email",
      required: true,
      requiredMessage: "Vui lòng điền email",
    },
  },
  {
    name: "productInterest",
    colSpan: { laptop: 12 },
    variant: "default",
    blockName: "text-field",
    validation: undefined,
    textFieldProps: {
      label: "Sản phẩm quan tâm",
    },
  },
  {
    name: "content",
    colSpan: { laptop: 12 },
    variant: "multiline",
    blockName: "text-field",
    validation: undefined,
    textFieldProps: {
      rows: 5,
      label: "Nội dung liên hệ *",
      required: true,
      requiredMessage: "Vui lòng điền nội dung",
    },
  },
];

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const ContactFormDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): ContactFormViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        backgroundColor: (desktop.backgroundColor as string) || DEFAULT_BG,
        title: (desktop.title as string) || "",
        heading1: (desktop.heading1 as string) || "",
        description: (desktop.description as string) || "",
        contentPosition: (desktop.contentPosition as ContactFormContentPosition) || "left",
        submitButtonText: (desktop.submitButtonText as string) || "Gửi Liên Hệ",
        submitButtonColor: (desktop.submitButtonColor as string) || DEFAULT_SUBMIT_COLOR,
      },
      responsive,
      viewMode,
      [
        "backgroundImage",
        "backgroundColor",
        "title",
        "heading1",
        "description",
        "contentPosition",
        "submitButtonText",
        "submitButtonColor",
      ],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      backgroundColor: String(resolved.backgroundColor || DEFAULT_BG),
      title: String(resolved.title || ""),
      heading1: String(resolved.heading1 || ""),
      description: String(resolved.description || ""),
      contentPosition: (resolved.contentPosition as ContactFormContentPosition) || "left",
      submitButtonText: String(resolved.submitButtonText || "Gửi Liên Hệ"),
      submitButtonColor: String(resolved.submitButtonColor || DEFAULT_SUBMIT_COLOR),
    };
  }, [desktop, responsive, viewMode]);

  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const textFirst = (data.contentPosition || "left") === "left";
  const submitColor = data.submitButtonColor || DEFAULT_SUBMIT_COLOR;

  const textBlock = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {data.title ? (
        <Typography
          component="p"
          sx={{
            color: "text.secondary",
            fontSize: { xs: 13, laptop: 14 },
            fontWeight: 600,
            letterSpacing: "0.03em",
          }}
        >
          {data.title}
        </Typography>
      ) : null}

      {data.heading1 ? (
        <Typography
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 24, tablet: 28, laptop: 32 },
            lineHeight: 1.2,
            color: "text.primary",
            letterSpacing: "-0.02em",
          }}
        >
          {data.heading1}
        </Typography>
      ) : null}

      {data.description ? (
        <Typography
          component="div"
          sx={{
            fontSize: { xs: 14, laptop: 15 },
            lineHeight: 1.75,
            color: "text.secondary",
            whiteSpace: "pre-line",
          }}
        >
          {data.description}
        </Typography>
      ) : null}
    </Box>
  );

  const formBlock = (
    <Box
      sx={{
        "& form": { margin: 0 },
        bgcolor: backgroundUrl ? "rgba(255,255,255,0.92)" : "background.paper",
        borderRadius: { xs: 1, laptop: 1.5 },
        p: { xs: 2, laptop: 3 },
        boxShadow: backgroundUrl ? "0 2px 16px rgba(0,0,0,0.06)" : "none",
      }}
    >
      <FormWrapperDefault
        columnTotal={12}
        fields={[...CONTACT_FORM_FIELDS]}
        buttonSubmitText={data.submitButtonText}
        submitButtonAlignment="flex-start"
        gridProps={{ spacing: { mobile: 1.5, tablet: 2, laptop: 2 } }}
        buttonSubmitProps={{
          sx: {
            color: "#fff",
            boxShadow: "none",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            px: 3,
            backgroundColor: submitColor,
            "&:hover": {
              boxShadow: "none",
              backgroundColor: submitColor,
              filter: "brightness(0.92)",
            },
          },
        }}
      />
    </Box>
  );

  return (
    <Box
      component="section"
      id="contact-form"
      sx={{
        position: "relative",
        py: { xs: 5, laptop: 7 },
        bgcolor: data.backgroundColor || DEFAULT_BG,
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {backgroundUrl ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.88)",
          }}
        />
      ) : null}

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 3, laptop: 5 }} alignItems="flex-start">
          {textFirst ? (
            <>
              <Grid item xs={12} laptop={6}>
                {textBlock}
              </Grid>
              <Grid item xs={12} laptop={6}>
                {formBlock}
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} laptop={6}>
                {formBlock}
              </Grid>
              <Grid item xs={12} laptop={6}>
                {textBlock}
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactFormDefaultV2;
